import {
  CustomisationIcon,
  UnlimitedIcon,
  ProcessingIcon,
  UsageIcon,
} from "./Icons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { BaseUser } from "../types";
import { useAuth } from "../contexts/AuthContext";
import { useBillingCheckout } from "../hooks/useBillingCheckout";

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: BaseUser | null;
}

function PaywallModal({ isOpen, onClose }: PaywallModalProps) {
  const { currentUser } = useAuth();
  const { checkoutPending, checkoutError, checkoutResolution, startCheckout } =
    useBillingCheckout();

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upgrade to Pro</DialogTitle>
          <DialogDescription>
            Get unlimited generations. Upgrade to Pro today
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-6 text-foreground">
          <div className="mb-6 mt-2 p-2 bg-main/10 rounded-lg border-2 border-border text-center w-max m-auto neo-shadow">
            <div className="relative text-main text-center flex justify-center items-center gap-1">
              <div className="text-3xl font-semibold">$10</div>
              <div className="text-xl font-normal">/month</div>
            </div>
          </div>

          <div className="flex gap-4 items-center mt-6">
            <div className="flex-1 h-px border border-border"></div>
            <div className="text-center text-foreground/70 text-sm text-nowrap">
              Included in the plan
            </div>
            <div className="flex-1 h-px border border-border"></div>
          </div>

          <div className="p-6 my-6 bg-secondary-background rounded-2xl border-2 border-border neo-shadow">
            <ul className="text-left space-y-3">
              <div className="text-foreground flex gap-2">
                <UnlimitedIcon />
                <span className="text-base">Unlimited 3D generations</span>
              </div>
              <div className="text-foreground flex gap-2">
                <CustomisationIcon />
                <span className="text-base">
                  Advanced customization options
                </span>
              </div>
              <div className="text-foreground flex gap-2">
                <ProcessingIcon />
                <span className="text-base">Priority processing</span>
              </div>
              <div className="text-foreground flex gap-2">
                <UsageIcon />
                <span className="text-base">Commercial usage rights</span>
              </div>
            </ul>
          </div>

          <div className="space-y-4">
            {checkoutError && (
              <p
                role="alert"
                className="text-sm text-red-600"
                data-testid="checkout-error"
              >
                {checkoutError}
              </p>
            )}
            {!checkoutResolution && (
              <Button
                onClick={startCheckout}
                disabled={checkoutPending}
                className="w-full"
              >
                {checkoutPending
                  ? "Opening secure checkout…"
                  : currentUser
                    ? "Upgrade to Pro"
                    : "Sign in to upgrade"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PaywallModal;
